import { toast } from '@/components/ToastNotification'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api } from '@/lib/api'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'

interface ConfigEmailState {
  host: string
  porta: number
  portaSsl: number
  secure: boolean
  usuario: string
  senha: string
  remetente: string
  ativo: boolean
}

const initialConfigEmail: ConfigEmailState = {
  host: '',
  porta: 587,
  portaSsl: 465,
  secure: false,
  usuario: '',
  senha: '',
  remetente: '',
  ativo: true
}

interface SistemaTabProps {
  getIdEmpresa: () => number | null
}

export function SistemaTab({ getIdEmpresa }: SistemaTabProps) {
  const [configEmail, setConfigEmail] =
    useState<ConfigEmailState>(initialConfigEmail)
  const [loadingConfigEmail, setLoadingConfigEmail] = useState(false)
  const [savingConfigEmail, setSavingConfigEmail] = useState(false)
  const [testingConfigEmail, setTestingConfigEmail] = useState(false)

  const loadConfigEmail = async () => {
    const id = getIdEmpresa()
    if (!id) return
    setLoadingConfigEmail(true)
    try {
      const res = await api.get(`/api/empresas/${id}/config-email`)
      const c = res.result
      if (c) {
        setConfigEmail({
          host: c.host ?? '',
          porta: c.porta ?? 587,
          portaSsl: c.secure ? c.porta ?? 465 : 465,
          secure: c.secure ?? false,
          usuario: c.usuario ?? '',
          senha: c.senha ?? '',
          remetente: c.remetente ?? '',
          ativo: c.ativo ?? true
        })
      }
    } catch (_) {
      // Sem config salva ainda
    } finally {
      setLoadingConfigEmail(false)
    }
  }

  useEffect(() => {
    loadConfigEmail()
  }, [getIdEmpresa])

  const handleSaveConfigEmail = async () => {
    const id = getIdEmpresa()
    if (!id) {
      toast.error('Empresa não identificada.')
      return
    }
    if (configEmail.remetente && !/^\S+@\S+\.\S+$/.test(configEmail.remetente)) {
      toast.error('O E-mail remetente fornecido é inválido.')
      return
    }
    setSavingConfigEmail(true)
    try {
      const portaEfetiva = configEmail.secure
        ? configEmail.portaSsl || 465
        : configEmail.porta
      await api.put(`/api/empresas/${id}/config-email`, {
        host: configEmail.host || undefined,
        porta: portaEfetiva,
        secure: configEmail.secure,
        usuario: configEmail.usuario || undefined,
        senha: configEmail.senha || undefined,
        remetente: configEmail.remetente || undefined,
        ativo: configEmail.ativo
      })
      toast.success('Configuração de e-mail salva com sucesso.')
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
        'Erro ao salvar configuração de e-mail.'
      )
    } finally {
      setSavingConfigEmail(false)
    }
  }

  const handleTestConfigEmail = async () => {
    const id = getIdEmpresa()
    if (!id) {
      toast.error('Empresa não identificada.')
      return
    }
    if (!configEmail.host || !configEmail.usuario || !configEmail.senha) {
      toast.error('Preencha host, usuário e senha para testar a conexão.')
      return
    }
    if (configEmail.remetente && !/^\S+@\S+\.\S+$/.test(configEmail.remetente)) {
      toast.error('O E-mail remetente fornecido é inválido.')
      return
    }
    setTestingConfigEmail(true)
    try {
      const portaEfetiva = configEmail.secure
        ? configEmail.portaSsl || 465
        : configEmail.porta
      const response = await api.post(
        `/api/empresas/${id}/config-email/testar`,
        {
          host: configEmail.host || undefined,
          porta: portaEfetiva,
          secure: configEmail.secure,
          usuario: configEmail.usuario || undefined,
          senha: configEmail.senha || undefined,
          remetente: configEmail.remetente || undefined
        }
      )
      toast.success(
        response?.result?.message || 'Conexão SMTP validada com sucesso.'
      )
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
        'Falha ao testar conexão de e-mail.'
      )
    } finally {
      setTestingConfigEmail(false)
    }
  }

  return (
    <Card className="p-6">
      <div className="space-y-4">
        <h3 className="font-semibold text-lg">
          Configuração de envio de e-mail
        </h3>
        <p className="text-sm text-muted-foreground">
          Configure o servidor SMTP para envio de e-mails (boas-vindas,
          notificações). Se não preencher, o sistema usará as variáveis de
          ambiente do servidor.
        </p>
        {loadingConfigEmail ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Carregando...
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="email-host">Host SMTP</Label>
              <Input
                id="email-host"
                value={configEmail.host}
                onChange={e =>
                  setConfigEmail({ ...configEmail, host: e.target.value })
                }
                placeholder="smtp.exemplo.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email-porta">Porta</Label>
              <Input
                id="email-porta"
                type="number"
                value={configEmail.porta}
                onChange={e =>
                  setConfigEmail({
                    ...configEmail,
                    porta: parseInt(e.target.value, 10) || 587
                  })
                }
                placeholder="587"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email-porta-ssl">Porta SSL/TLS</Label>
              <Input
                id="email-porta-ssl"
                type="number"
                value={configEmail.portaSsl}
                onChange={e =>
                  setConfigEmail({
                    ...configEmail,
                    portaSsl: parseInt(e.target.value, 10) || 465
                  })
                }
                placeholder="465"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="email-usuario">
                Usuário (e-mail ou login SMTP)
              </Label>
              <Input
                id="email-usuario"
                type="email"
                value={configEmail.usuario}
                onChange={e =>
                  setConfigEmail({ ...configEmail, usuario: e.target.value })
                }
                placeholder="usuario@smtp.com"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="email-senha">Senha</Label>
              <Input
                id="email-senha"
                type="password"
                value={configEmail.senha}
                onChange={e =>
                  setConfigEmail({ ...configEmail, senha: e.target.value })
                }
                placeholder="••••••••"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="email-remetente">
                E-mail remetente (&quot;De&quot;)
              </Label>
              <Input
                id="email-remetente"
                type="email"
                value={configEmail.remetente}
                onChange={e =>
                  setConfigEmail({ ...configEmail, remetente: e.target.value })
                }
                placeholder="noreply@empresa.com"
              />
            </div>
            <div className="flex items-center space-x-2 md:col-span-2">
              <input
                type="checkbox"
                id="email-secure"
                checked={configEmail.secure}
                onChange={e =>
                  setConfigEmail({
                    ...configEmail,
                    secure: e.target.checked
                  })
                }
                className="rounded"
              />
              <Label htmlFor="email-secure">
                Conexão segura (SSL/TLS)
              </Label>
            </div>
            <div className="flex items-center space-x-2 md:col-span-2">
              <input
                type="checkbox"
                id="email-ativo"
                checked={configEmail.ativo}
                onChange={e =>
                  setConfigEmail({
                    ...configEmail,
                    ativo: e.target.checked
                  })
                }
                className="rounded"
              />
              <Label htmlFor="email-ativo">
                Usar esta configuração para envio de e-mails
              </Label>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-4">
          <Button
            variant="outline"
            onClick={handleTestConfigEmail}
            disabled={
              testingConfigEmail ||
              savingConfigEmail ||
              loadingConfigEmail
            }
          >
            {testingConfigEmail ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Testando...
              </>
            ) : (
              'Testar conexão'
            )}
          </Button>
          <Button
            onClick={handleSaveConfigEmail}
            disabled={
              savingConfigEmail || loadingConfigEmail || testingConfigEmail
            }
          >
            {savingConfigEmail ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : (
              'Salvar'
            )}
          </Button>
        </div>
      </div>
    </Card>
  )
}
